import { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Card } from './ui/card';
import { liveChatAPI } from '../api';
import { useAuth } from '../context/AuthContext';
import { playNotificationSound } from '../utils/notificationSound';
import { toast } from 'sonner';
import { 
  MessageCircle, X, Send, Minimize2, Maximize2, 
  User, Loader2, CheckCircle, Clock, Volume2, VolumeX, Headphones
} from 'lucide-react';

/**
 * CustomerChatWidget - Live chat for customer portal
 * Allows customers to chat with support agents
 */
const SUPPORT_EMAIL = 'support@chinookmanufacturing.com';

export const CustomerChatWidget = () => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [agentAvailable, setAgentAvailable] = useState(false);
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [showBubble, setShowBubble] = useState(true);
  const [showEmailPrompt, setShowEmailPrompt] = useState(false);
  const messagesEndRef = useRef(null);
  const previousMessageCount = useRef(0);
  const pollInterval = useRef(null);
  const emailPromptTimer = useRef(null);

  const agentConnected = messages.some(m => m.sender_type === 'agent');

  // Prompt to email support if no agent connects within 60 seconds
  useEffect(() => {
    if (session?.id) {
      setShowEmailPrompt(false);
      if (emailPromptTimer.current) clearTimeout(emailPromptTimer.current);
      emailPromptTimer.current = setTimeout(() => setShowEmailPrompt(true), 60000);
    }
    return () => { if (emailPromptTimer.current) clearTimeout(emailPromptTimer.current); };
  }, [session]);

  useEffect(() => {
    if (agentConnected) {
      setShowEmailPrompt(false);
      if (emailPromptTimer.current) clearTimeout(emailPromptTimer.current);
    }
  }, [agentConnected]);

  // Check agent availability
  const checkAvailability = useCallback(async () => {
    try {
      const res = await liveChatAPI.getAvailableAgents();
      const agents = res.data.agents || [];
      setAgentAvailable(agents.length > 0);
    } catch (err) {
      console.debug('Could not check agent availability:', err);
    }
  }, []);

  // Fetch messages for active session
  const fetchMessages = useCallback(async () => {
    if (!session?.id) return;
    
    try {
      const res = await liveChatAPI.getMessages(session.id);
      const newMessages = Array.isArray(res.data) ? res.data : (res.data.messages || []);
      
      // Play sound for new messages from agent
      if (newMessages.length > previousMessageCount.current && previousMessageCount.current > 0) {
        const lastMessage = newMessages[newMessages.length - 1];
        if (lastMessage.sender_type === 'agent' && soundEnabled) {
          playNotificationSound('chat', 0.3);
          
          // Show toast if chat is minimized
          if (isMinimized) {
            toast.info('New message from support', {
              description: lastMessage.content.substring(0, 50) + '...',
              action: {
                label: 'View',
                onClick: () => setIsMinimized(false)
              }
            });
          }
        }
      }
      
      previousMessageCount.current = newMessages.length;
      setMessages(newMessages);
    } catch (err) {
      console.error('Failed to fetch messages:', err);
    }
  }, [session, soundEnabled, isMinimized]);

  // Initial load and polling
  useEffect(() => {
    checkAvailability();
    const availInterval = setInterval(checkAvailability, 30000);
    
    return () => {
      clearInterval(availInterval);
      if (pollInterval.current) clearInterval(pollInterval.current);
    };
  }, [checkAvailability]);

  // Poll for messages when session is active
  useEffect(() => {
    if (session?.id && isOpen) {
      fetchMessages();
      pollInterval.current = setInterval(fetchMessages, 2500);
    } else if (pollInterval.current) {
      clearInterval(pollInterval.current);
    }
    
    return () => {
      if (pollInterval.current) clearInterval(pollInterval.current);
    };
  }, [session, isOpen, fetchMessages]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Start a new chat session
  const startChat = async () => {
    setLoading(true);
    try {
      // Generate a unique visitor ID for this session
      const visitorId = `portal_${user?.id || 'anonymous'}_${Date.now()}`;
      
      const res = await liveChatAPI.createSession({
        visitor_id: visitorId,
        visitor_name: `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || user?.email,
        visitor_email: user?.email,
        initial_message: 'Hello, I need help with the customer portal.',
        request_agent: true
      });
      
      setSession(res.data);
      try { localStorage.setItem('chinook_chat_session', res.data.id); } catch (e) { /* ignore */ }
      setIsOpen(true);
      setIsMinimized(false);
      setShowBubble(false);
      playNotificationSound('success', 0.2);
      toast.success('Connected to support! An agent will respond shortly.');
    } catch (err) {
      console.error('Chat connection error:', err);
      toast.error('Failed to connect to support. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Send a message
  const sendMessage = async (e) => {
    e?.preventDefault();
    const text = newMessage.trim();
    if (!text || !session?.id) return;
    
    setSending(true);
    // Optimistically show the message immediately
    const optimistic = {
      id: `tmp-${Date.now()}`,
      session_id: session.id,
      sender_type: 'visitor',
      content: text,
      created_at: new Date().toISOString(),
    };
    setMessages(prev => [...prev, optimistic]);
    setNewMessage('');
    try {
      await liveChatAPI.sendMessage(session.id, {
        content: text,
        sender_type: 'visitor'
      });
      fetchMessages();
    } catch (err) {
      toast.error('Failed to send message');
      setMessages(prev => prev.filter(m => m.id !== optimistic.id));
      setNewMessage(text);
    } finally {
      setSending(false);
    }
  };

  // Close chat
  const closeChat = async () => {
    if (session?.id) {
      try {
        await liveChatAPI.closeChat(session.id);
        toast.success('Chat ended. Thank you for contacting us!');
      } catch (err) {
        console.error('Failed to close chat:', err);
      }
    }
    setSession(null);
    setMessages([]);
    setIsOpen(false);
    setShowBubble(true);
    previousMessageCount.current = 0;
    try { localStorage.removeItem('chinook_chat_session'); } catch (e) { /* ignore */ }
  };

  // Restore an in-progress chat after a page refresh so the customer keeps
  // their conversation (and the agent's replies) instead of starting over.
  useEffect(() => {
    const savedId = (() => { try { return localStorage.getItem('chinook_chat_session'); } catch (e) { return null; } })();
    if (!savedId) return;
    (async () => {
      try {
        const res = await liveChatAPI.getSession(savedId);
        const s = res.data;
        if (s && s.status && s.status !== 'closed' && s.status !== 'ended') {
          setSession(s);
        } else {
          localStorage.removeItem('chinook_chat_session');
        }
      } catch (e) {
        try { localStorage.removeItem('chinook_chat_session'); } catch (err) { /* ignore */ }
      }
    })();
  }, []);

  return (
    <>
      {/* Floating Chat Button */}
      <div className="fixed bottom-6 right-6 z-40">
        {/* Welcome Bubble */}
        {showBubble && !isOpen && (
          <div className="absolute bottom-16 right-0 bg-white rounded-lg shadow-xl border p-4 w-72 animate-fade-in">
            <button 
              onClick={() => setShowBubble(false)} 
              className="absolute top-2 right-2 text-slate-400 hover:text-slate-600"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center flex-shrink-0">
                <Headphones className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="font-semibold text-slate-900">Need help?</p>
                <p className="text-sm text-muted-foreground mt-1">
                  {agentAvailable 
                    ? 'Our support team is online and ready to assist you!'
                    : 'Leave a message and we\'ll get back to you soon.'
                  }
                </p>
              </div>
            </div>
            <Button 
              className="w-full mt-4"
              onClick={() => {
                setShowBubble(false);
                startChat();
              }}
              disabled={loading}
            >
              {loading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <MessageCircle className="h-4 w-4 mr-2" />
              )}
              Start Chat
            </Button>
          </div>
        )}

        {/* Main Chat Button */}
        {!isOpen && (
          <Button
            size="lg"
            className={`rounded-full h-14 w-14 shadow-lg transition-all hover:scale-105 ${
              agentAvailable 
                ? 'bg-gradient-to-br from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700' 
                : 'bg-slate-600 hover:bg-slate-700'
            }`}
            onClick={() => {
              if (session) {
                setIsOpen(true);
                setIsMinimized(false);
              } else {
                startChat();
              }
            }}
            disabled={loading}
            data-testid="customer-chat-button"
          >
            {loading ? (
              <Loader2 className="h-6 w-6 animate-spin" />
            ) : (
              <>
                <MessageCircle className="h-6 w-6" />
                {agentAvailable && (
                  <span className="absolute -top-1 -right-1 h-4 w-4 bg-green-400 rounded-full border-2 border-white animate-pulse" />
                )}
              </>
            )}
          </Button>
        )}

        {/* Chat Window */}
        {isOpen && (
          <Card className={`w-80 sm:w-96 shadow-2xl overflow-hidden ${isMinimized ? 'h-14' : 'h-[500px]'} transition-all duration-200`}>
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white p-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <Headphones className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-semibold">Customer Support</p>
                  {messages.some(m => m.sender_type === 'agent') ? (
                    <p className="text-xs text-blue-100 flex items-center gap-1">
                      <CheckCircle className="h-3 w-3" /> Support agent connected
                    </p>
                  ) : (
                    <p className="text-xs text-blue-100 flex items-center gap-1">
                      <Clock className="h-3 w-3" /> Waiting for an agent...
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setSoundEnabled(!soundEnabled)}
                  className="p-1.5 hover:bg-white/20 rounded transition-colors"
                  title={soundEnabled ? 'Mute notifications' : 'Unmute notifications'}
                >
                  {soundEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
                </button>
                <button
                  onClick={() => setIsMinimized(!isMinimized)}
                  className="p-1.5 hover:bg-white/20 rounded transition-colors"
                >
                  {isMinimized ? <Maximize2 className="h-4 w-4" /> : <Minimize2 className="h-4 w-4" />}
                </button>
                <button
                  onClick={closeChat}
                  className="p-1.5 hover:bg-white/20 rounded transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Messages */}
            {!isMinimized && (
              <>
                <ScrollArea className="h-[380px] p-4 bg-slate-50">
                  {messages.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <MessageCircle className="h-8 w-8 text-blue-500" />
                      </div>
                      <p className="font-medium text-slate-700">Welcome to Support!</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Type a message to start the conversation.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {messages.map((msg, idx) => (
                        <div
                          key={idx}
                          className={`flex ${msg.sender_type === 'visitor' ? 'justify-end' : 'justify-start'}`}
                        >
                          {msg.sender_type === 'agent' && (
                            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-2 flex-shrink-0">
                              <User className="h-4 w-4 text-blue-600" />
                            </div>
                          )}
                          <div
                            className={`max-w-[75%] p-3 rounded-2xl ${
                              msg.sender_type === 'visitor'
                                ? 'bg-blue-600 text-white rounded-br-md'
                                : msg.sender_type === 'system'
                                ? 'bg-slate-200 text-slate-600 text-center w-full text-xs py-2'
                                : 'bg-white text-slate-800 shadow-sm border rounded-bl-md'
                            }`}
                          >
                            {msg.sender_type === 'agent' && msg.sender_name && (
                              <p className="text-xs font-medium text-blue-600 mb-1">{msg.sender_name}</p>
                            )}
                            <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                            <p className={`text-xs mt-1 ${msg.sender_type === 'visitor' ? 'text-blue-200' : 'text-slate-400'}`}>
                              {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </div>
                  )}
                </ScrollArea>

                {/* Email fallback prompt after 60s with no agent */}
                {showEmailPrompt && !agentConnected && (
                  <div className="px-3 py-2 bg-amber-50 border-t border-b border-amber-200 text-xs text-amber-800" data-testid="chat-email-prompt">
                    Our agents are taking longer than usual. For a faster response, email us at{' '}
                    <a href={`mailto:${SUPPORT_EMAIL}`} className="font-semibold underline" data-testid="chat-support-email">{SUPPORT_EMAIL}</a>.
                  </div>
                )}

                {/* Input */}
                <form onSubmit={sendMessage} className="p-3 border-t bg-white flex gap-2">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type your message..."
                    disabled={sending}
                    className="flex-1"
                    data-testid="customer-chat-input"
                  />
                  <Button 
                    type="submit" 
                    size="icon" 
                    disabled={sending || !newMessage.trim()}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  </Button>
                </form>
              </>
            )}
          </Card>
        )}
      </div>

      {/* Animations */}
      <style>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default CustomerChatWidget;
